import React from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import PrimaryEventImage from '../PrimaryEventImage/PrimaryEventImage';
import { AspectRatioWrapper } from '../../components';
import { appendReferrerDiscovery } from '../../util/promotixDiscoveryLinks';
import { formatMoney } from '../../util/currency';
import { primaryAmountToMoney, PROMOTIX_DEFAULT_IMAGE_URL } from '../../util/primaryEventMapListing';
import { primaryEventCardDateLine } from '../../util/eventDateRangeFilter';
import { optimizedImageSources } from '../../util/imageOptimizer';
import { richText } from '../../util/richText';

import css from './PrimaryEventCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const isFreePrimaryPrice = (lo, hi) => {
  const z = v =>
    v == null ||
    v === '' ||
    String(v) === '0' ||
    String(v) === '0.00' ||
    String(v) === '0.0';
  return z(lo) && z(hi);
};

export const PrimaryEventCardComponent = props => {
  const { className, rootClassName, intl, event, renderSizes } = props;
  const config = useConfiguration();
  const currency = config.currency;
  const { aspectWidth = 1, aspectHeight = 1 } = config.layout.listingImage;
  const classes = classNames(rootClassName || css.root, className);
  const href = appendReferrerDiscovery(event.social_share_url);

  const lo = event.min_price;
  const hi = event.max_price;

  const priceDisplay = (() => {
    if (lo == null && hi == null) {
      return null;
    }
    if (isFreePrimaryPrice(lo, hi)) {
      return intl.formatMessage({ id: 'PrimaryEventCard.freeOrRsvp' });
    }
    const loMoney = lo != null && lo !== '' ? primaryAmountToMoney(lo, currency) : null;
    const hiMoney = hi != null && hi !== '' ? primaryAmountToMoney(hi, currency) : null;
    if (loMoney && hiMoney) {
      const a = formatMoney(intl, loMoney);
      const b = formatMoney(intl, hiMoney);
      if (a !== b) {
        return `${a} – ${b}`;
      }
    }
    const single = loMoney || hiMoney;
    return single ? formatMoney(intl, single) : null;
  })();

  const dateLine = primaryEventCardDateLine(intl, event);

  if (!href) {
    return null;
  }

  const { src: imageSrc, srcSet: imageSrcSet } = optimizedImageSources(
    event.image || PROMOTIX_DEFAULT_IMAGE_URL
  );

  return (
    <a className={classes} href={href} target="_blank" rel="noopener noreferrer">
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
      >
        <PrimaryEventImage
          className={css.rootForImage}
          alt={event.event_title || ''}
          src={imageSrc}
          srcSet={imageSrcSet}
          sizes={renderSizes}
        />
      </AspectRatioWrapper>
      <div className={css.info}>
        {priceDisplay ? (
          <div className={css.price}>
            <div className={css.priceValue} title={priceDisplay}>
              {priceDisplay}
            </div>
          </div>
        ) : null}
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(event.event_title || '', {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          <div className={css.sellerInfo}>
            <FormattedMessage id="PrimaryEventCard.officialSeller" />
          </div>
          {dateLine ? <div className={css.cardMetaLine}>{dateLine}</div> : null}
        </div>
      </div>
    </a>
  );
};

PrimaryEventCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
};

PrimaryEventCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  event: (props, propName) => {
    const v = props[propName];
    if (!v || typeof v !== 'object') {
      return new Error('PrimaryEventCard requires event object');
    }
    return null;
  },
  renderSizes: string.isRequired,
};

const PrimaryEventCard = injectIntl(PrimaryEventCardComponent);

export default PrimaryEventCard;
